/**
 * 메뉴 설정 관리 React Query 훅.
 * 트리 조회·상세·CRUD·순서 저장. 성공 시 트리 캐시를 교체하거나 무효화한다.
 */
import { menusService } from '@/entities/menu/api/menus.api';
import type {
  AdminMenuTreeReorderRequest,
  AdminMenuUpsertRequest,
} from '@/entities/menu/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

export const menuKeys = {
  all: ['menus'] as const,
  tree: () => [...menuKeys.all, 'tree'] as const,
  detail: (id: number) => [...menuKeys.all, 'detail', id] as const,
};

/**
 * 전체 메뉴 트리.
 * 메뉴 설정 관리 화면 전용. 사이드바는 `/admin/auth/me` permissions를 사용한다.
 * 403(권한 없음)은 재시도하지 않는다.
 */
export const useMenuTree = (enabled = true) =>
  useQuery({
    queryKey: menuKeys.tree(),
    queryFn: () => menusService.getTree(),
    enabled,
    retry: (failureCount, error) => {
      if (isAxiosError(error) && error.response?.status === 403) {
        return false;
      }
      return failureCount < 2;
    },
  });

export const useMenuDetail = (menuId: number | null) =>
  useQuery({
    queryKey: menuId != null ? menuKeys.detail(menuId) : [],
    queryFn: async () => {
      if (menuId == null) throw new Error('menuId is required');
      return menusService.getDetail(menuId);
    },
    enabled: menuId != null && menuId > 0,
  });

export const useCreateMenu = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminMenuUpsertRequest) => menusService.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuKeys.tree() });
    },
  });
};

export const useUpdateMenu = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      menuId,
      body,
    }: {
      menuId: number;
      body: AdminMenuUpsertRequest;
    }) => menusService.update(menuId, body),
    onSuccess: (data) => {
      queryClient.setQueryData(menuKeys.detail(data.id), data);
      queryClient.invalidateQueries({ queryKey: menuKeys.tree() });
    },
  });
};

export const useDeleteMenu = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (menuId: number) => menusService.remove(menuId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuKeys.tree() });
    },
  });
};

/** 저장 성공 시 응답 트리로 캐시를 교체한다 */
export const useReorderMenuTree = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminMenuTreeReorderRequest) =>
      menusService.reorderTree(body),
    onSuccess: (data) => {
      queryClient.setQueryData(menuKeys.tree(), data);
    },
  });
};
